import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type LabelBadgeDot =
  | 'red'
  | 'green'
  | 'cyan'
  | 'blue'
  | 'orange'
  | 'yellow'
  | 'purple'
  | 'magenta';

// Maps DS hues to their 500-level colorscale token. Dark mode steps these down
// via a brightness filter (see :host-context(.dark) below) rather than a
// second map, matching the design-system spec.
const DOT_COLOR: Record<LabelBadgeDot, string> = {
  red: 'var(--colorscales-red-500)',
  green: 'var(--colorscales-green-500)',
  cyan: 'var(--colorscales-cyan-500)',
  blue: 'var(--colorscales-blue-500)',
  orange: 'var(--colorscales-orange-500)',
  yellow: 'var(--colorscales-yellow-500)',
  purple: 'var(--colorscales-purple-500)',
  magenta: 'var(--colorscales-magenta-500)',
};

/**
 * LabelBadge — a 24px badge holding one word or a short phrase, with an
 * optional coloured dot carrying status. Sits between the 16px status
 * `Badge` (dot only) and `Pill` (removable filter chip). Non-interactive.
 *
 * Ported from the "Label badge — hairline + dot" design-system spec.
 *
 * Usage:
 *   <app-label-badge dot="green">Online</app-label-badge>
 *   <app-label-badge>Draft</app-label-badge>  <!-- no dot: neutral/inactive -->
 *
 * Hue mapping: green normal · orange attention · red fault · blue scheduled
 * or informational · purple/magenta/cyan/yellow content type · no dot +
 * ink-2 for inactive. Content should be sentence case, no full stop, and
 * never wrap or truncate — anything longer than ~3 words belongs in the
 * cell or a tooltip instead.
 */
@Component({
  selector: 'app-label-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="pc-label-badge" [class.pc-label-badge--neutral]="!dot()">
      @if (dotColor()) {
        <span class="pc-label-badge__dot" [style.background]="dotColor()"></span>
      }
      <ng-content></ng-content>
    </span>
  `,
  styles: `
    .pc-label-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 24px;
      padding: 0 8px;
      border-radius: var(--radius-control);
      background: var(--surface-card);
      box-shadow: var(--shadow-hairline);
      color: var(--ink-1);
      font-family: var(--font-body, 'Open Sans', sans-serif);
      font-weight: 600;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: 0.01em;
      white-space: nowrap;
    }

    .pc-label-badge--neutral {
      color: var(--ink-2);
    }

    .pc-label-badge__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    :host-context(.dark) .pc-label-badge,
    :host-context([data-theme='dark']) .pc-label-badge {
      background: var(--surface-sunken);
      box-shadow: inset 0 0 0 1px var(--border-strong);
    }

    :host-context(.dark) .pc-label-badge__dot,
    :host-context([data-theme='dark']) .pc-label-badge__dot {
      filter: brightness(1.6);
    }
  `,
})
export class LabelBadge {
  readonly dot = input<LabelBadgeDot | null>(null);

  protected readonly dotColor = computed(() => {
    const dot = this.dot();
    return dot ? DOT_COLOR[dot] : null;
  });
}
